import { exchangeRateService } from '@flows.cash/core';

// Define the return type explicitly
interface ExchangeRates {
  base: string;
  rates: Record<string, number>;
  timestamp: number;
}

/**
 * Fetch latest exchange rates, either from cache or from the API
 * @returns Exchange rates object or null if failed
 */
export async function fetchLatestRates(): Promise<ExchangeRates | null> {
  try {
    const rates = await exchangeRateService.fetchLatestRates();
    if (rates) {
      console.log('Exchange rates updated successfully');
    }
    return rates;
  } catch (error) {
    console.error('Error fetching exchange rates:', error);
    return null;
  }
}

/**
 * Convert an amount from one currency to another
 * @param amount Amount to convert
 * @param fromCurrency Source currency code
 * @param toCurrency Target currency code
 * @param rates Exchange rates object
 * @returns Converted amount
 */
export function convertCurrency(
  amount: number,
  fromCurrency: string,
  toCurrency: string,
  rates: Record<string, number>
): number {
  try {
    // If currencies are the same, no conversion needed
    if (fromCurrency === toCurrency) {
      return amount;
    }
    
    // List of currencies that might not be supported by the exchange rate API
    const unsupportedCurrencies = ['IQD', 'IRR', 'SYP', 'VES'];
    
    // Check if we're dealing with unsupported currencies
    if (unsupportedCurrencies.includes(fromCurrency) || unsupportedCurrencies.includes(toCurrency)) {
      // Use fallback rates for common pairs
      if (fromCurrency === 'USD' && toCurrency === 'IQD') {
        return amount * 1309.5; // Approximate USD to IQD rate
      } else if (fromCurrency === 'IQD' && toCurrency === 'USD') {
        return amount * 0.00076; // Approximate IQD to USD rate
      }
      
      // For other unsupported currency pairs, log a warning but return a default conversion
      console.warn(`Using fallback conversion for unsupported currency pair: ${fromCurrency} to ${toCurrency}`);
      
      // Return the original amount as a fallback (user should enter manually)
      return amount;
    }
    
    // Get the conversion rate from the exchange rate service
    const convertedAmount = exchangeRateService.convertCurrency(amount, fromCurrency, toCurrency, rates);
    
    // Round to 4 decimal places
    return Math.round(convertedAmount * 10000) / 10000;
  } catch (error) {
    console.error('Error converting currency:', error);
    
    // Return the original amount as a fallback
    return amount;
  }
} 